"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

type TwoPanelListsBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  left_title?: string;
  left_items?: string;
  right_title?: string;
  right_items?: string;
  footnote?: string;
  bg_color?: unknown;
};

function TickIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="mt-0.5 size-5 shrink-0" aria-hidden>
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path
        d="M5.8 10.3 8.6 13l5.6-6"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="mt-0.5 size-5 shrink-0" aria-hidden>
      <path
        d="M8.6 3.1 1.9 14.6A1.6 1.6 0 0 0 3.3 17h13.4a1.6 1.6 0 0 0 1.4-2.4L11.4 3.1a1.6 1.6 0 0 0-2.8 0Z"
        fill="currentColor"
      />
      <path d="M10 8v3.2m0 2.4h.01" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function Panel({
  title,
  items,
  tone,
}: {
  title?: string;
  items: string[];
  tone: "positive" | "alert";
}) {
  if (items.length === 0 && !title) return null;
  const positive = tone === "positive";
  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-2xl p-6 lg:p-8",
        positive ? "bg-teal-01" : "bg-brand-orange-01/25",
      )}
    >
      {title && (
        <h3
          className={cn(
            "text-h6 font-semibold tracking-[-0.01em]",
            positive ? "text-teal-08" : "text-grey-09",
          )}
        >
          {title}
        </h3>
      )}
      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <li key={item} className="text-grey-08 flex gap-3 text-base leading-relaxed">
            <span className={positive ? "text-teal-06" : "text-brand-orange-02"}>
              {positive ? <TickIcon /> : <AlertIcon />}
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

const lines = (value?: string) =>
  (value || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

export default function TwoPanelLists({ blok }: { blok: TwoPanelListsBlok }) {
  const surface = surfaceFrom(blok.bg_color);
  const left = lines(blok.left_items);
  const right = lines(blok.right_items);
  if (left.length === 0 && right.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 lg:gap-12">
        {(blok.heading || blok.intro) && (
          <div className="flex flex-col gap-5">
            {blok.heading && (
              <h2 className={cn("text-section", headingOn(surface, "text-teal-07"))}>
                {blok.heading}
              </h2>
            )}
            {blok.intro && (
              <p
                className={cn(
                  "max-w-[943px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl",
                  onSurface(surface, "text-white/85", "text-grey-08", "text-grey-08"),
                )}
              >
                {blok.intro}
              </p>
            )}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
          <Panel title={blok.left_title} items={left} tone="positive" />
          <Panel title={blok.right_title} items={right} tone="alert" />
        </div>

        {blok.footnote && (
          <p
            className={cn(
              "text-base leading-relaxed",
              onSurface(surface, "text-white/70", "text-grey-06", "text-grey-06"),
            )}
          >
            {blok.footnote}
          </p>
        )}
      </div>
    </section>
  );
}
